import { Component, Input, ViewChild } from '@angular/core';
import { AlertController, IonAccordionGroup, IonDatetime, ModalController } from '@ionic/angular';

import { AlgoliaHit, AlgoliaInstanceNames } from '../../constants/algolia';
import { DateWithTimezone, AdminOpenHouseModalData, AdminOpenHouseModalOutput, OpenHouseEventStatus } from '../../types';
import { AlgoliaSearchChipSelect } from '../algolia-search-chip-select/algolia-search-chip-select';
import { getDateWithTimezone } from '../../../utils/dates';
import { AuthService } from '../../services/auth.service';
import moment from 'moment';
import { FullAgentEventRequestEntry } from '../../app.models';

@Component({
  selector: 'open-house-schedule-new-event',
  templateUrl: './open-house-schedule-new-event.html',
  styleUrls: ['./open-house-schedule-new-event.scss'],
  standalone: false,
})
export class OpenHouseSchedulerNewEvent {
  OpenhouseEventStatus = OpenHouseEventStatus;

  @Input('eventData') eventData: AdminOpenHouseModalData;

  selectedAgent: AlgoliaHit;

  @ViewChild('agentSearch', { static: true }) agentSearchComponent: AlgoliaSearchChipSelect;
  @ViewChild('accordionGroup') accordionGroup: IonAccordionGroup;

  assignmentType: 'assign' | 'claim' | 'request' = 'assign';
  eventInternalNote: string;

  startDate: string;
  endDate: string;

  eventId: string;
  status: OpenHouseEventStatus;

  existingEvent = false;

  instanceName = AlgoliaInstanceNames.open_house_scheduler_search_paired_agents;

  // Timepicker code

  // Actual time values, ex: "12:30". 24h format
  startTime: string;
  endTime: string;

  // Actual date value
  date: string;

  startTimePickerComponent: any;
  endTimePickerComponent: any;

  startIsInvalid = false;
  endIsInvalid = false;

  constructor(private modalCtrl: ModalController, private alertCtrl: AlertController, private auth: AuthService) {}

  async ngAfterContentInit() {
    const { agent, eventId, status, requiresApproval, internalNote } = this.eventData;

    if (agent) {
      this.selectedAgent = agent;

      const agentImageURL = this.eventData.agentImageURL;
      const agentAvatarName = this.eventData.agentAvatarName;

      // TODO: Try to remove
      setTimeout(() => this.agentSearchComponent.selectAgent(agent, agentImageURL, agentAvatarName));
    }

    if (eventId) {
      this.existingEvent = true;
    }

    this.eventId = eventId;
    this.status = status;
    this.eventInternalNote = internalNote;

    // If the the event has notes in it, we want to show the advanced options section open by default
    if (internalNote) {
      setTimeout(() => this.accordionGroup.value = 'advanced-options');
    }

    if (status === OpenHouseEventStatus.assigned) {
      this.assignmentType = 'assign';
    } else if (status === OpenHouseEventStatus.free) {
      if (requiresApproval) {
        this.assignmentType = 'request';
      } else {
        this.assignmentType = 'claim';
      }
    } else {
      console.error('Unknown configuration for Open House Event', status);
    }

    // Timepicker code

    // Start glue code
    this.startTimePickerComponent = document.getElementById('startTimePicker');
    this.startTimePickerComponent.addEventListener('value-changed', (event) => {
      this.startTime = event.target.value;

      // The date being hardcoded doesn't matter as we only care about the time
      const currentEnd = moment(`2025-01-01 ${this.endTime}`);

      let newMinimunEndTime = moment(`2025-01-01 ${this.startTime}`).add(60, 'minutes');

      if (newMinimunEndTime.day() !== currentEnd.day()) {
        // We don't want to end up in the next day

        newMinimunEndTime = moment(`2025-01-01 23:59`);
      }

      const newMinimunEndTimeString = newMinimunEndTime.format('HH:mm');

      if (currentEnd.isBefore(newMinimunEndTime)) {
        this.endTime = newMinimunEndTimeString;
        this.endTimePickerComponent.value = newMinimunEndTimeString;
      }

      this.endTimePickerComponent.min = newMinimunEndTimeString;
    });
    this.startTimePickerComponent.addEventListener('invalid-changed', (event) => (this.startIsInvalid = event.target.invalid));

    // End glue code
    this.endTimePickerComponent = document.getElementById('endTimePicker');
    this.endTimePickerComponent.addEventListener('value-changed', (event) => (this.endTime = event.target.value));
    this.endTimePickerComponent.addEventListener('invalid-changed', (event) => {
      this.endIsInvalid = false;
      console.log('invalid time: ' + this.endTime);
    });

    // Set up date formatters to show am/pm
    this.startTimePickerComponent.i18n = this.amPmFormatting();
    this.endTimePickerComponent.i18n = this.amPmFormatting();

    // Set up values
    const start = this.eventData.start;
    const end = this.eventData.end;

    this.startTime = start.format('HH:mm');
    this.endTime = end.format('HH:mm');
    this.date = start.toISOString();

    this.startTimePickerComponent.value = this.startTime;
    this.endTimePickerComponent.value = this.endTime;

    // TODO: Remove this hack, initialize the ionic component properly
    setTimeout(() => ((document.getElementById('datePicker') as any).value = this.date));
  }

  async saveEvent() {
    return this._dimissModal();
  }

  _dimissModal() {
    const date = getDateWithTimezone(new Date(this.date), this.eventData.start.tz());
    const { start, end } = getStartAndEndDates(date, this.startTime, this.endTime);

    let requiresApproval;
    if (this.assignmentType === 'assign') {
      requiresApproval = false;
      this.status = OpenHouseEventStatus.assigned;
    } else if (this.assignmentType === 'claim') {
      requiresApproval = false;
      this.status = OpenHouseEventStatus.free;
      this.selectedAgent = undefined;
    } else if (this.assignmentType === 'request') {
      requiresApproval = true;
      this.status = OpenHouseEventStatus.free;
      this.selectedAgent = undefined;
    }

    const data: AdminOpenHouseModalOutput = {
      ...this.eventData,

      action: this.existingEvent ? 'update' : 'create',

      start,
      end,

      agent: this.selectedAgent,

      status: this.status,

      requiresApproval,
      internalNote: this.eventInternalNote,
    };

    return this.modalCtrl.dismiss(data);
  }

  async deleteEvent() {
    const confirmationAlert = await this.alertCtrl.create({
      header: 'Delete Event',
      message: 'Are you sure you want to delete this Open House Event?',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Delete',
          handler: () => this._deleteEvent(),
        },
      ],
    });

    await confirmationAlert.present();
  }

  _deleteEvent() {
    this.modalCtrl.dismiss({ action: 'delete' });
  }

  onResultClick(hit: AlgoliaHit | undefined) {
    this.selectedAgent = hit;

    // Hit undefined means the user cleared the selection
    if (hit) {
      this.assignmentType = 'assign';
    }
  }

  isFormValid() {
    if (this.assignmentType === 'assign' && !this.selectedAgent) {
      return false;
    }

    return !this.startIsInvalid && !this.endIsInvalid
  }

  advancedOptionsVisible = false;
  // We use this to add/remove a CSS class to make the "Advanced options" bottom border always be rounded
  onAdvancedOptionsChange(event) {
    this.advancedOptionsVisible = event.detail.value;
  }

  onAssignmentTypeChange(event: { srcElement: any; detail: { checked: boolean; value: 'assign' | 'claim' | 'request' } }) {
    // We don't allow the user to unselect an option
    if (!event.detail.checked) {
      // So we reselect the previous value
      event.srcElement.checked = true;
      return;
    }

    this.assignmentType = event.detail.value;

    if (this.assignmentType === 'claim' || this.assignmentType === 'request') {
      this.status = OpenHouseEventStatus.free;
      this.selectedAgent = undefined;
      this.agentSearchComponent.removeSelectedAgent();
    }
  }

  close() {
    this.modalCtrl.dismiss();
  }

  onDateSelected(calendarRef: IonDatetime) {
    this.date = calendarRef.value as string;
  }

  async approveRequest(request: FullAgentEventRequestEntry) {
    const update = await this.eventData.assignEventFn(request.agentId)

    this.agentSearchComponent.removeSelectedAgent();
    this.agentSearchComponent.selectAgent(update.agent, update.agentImageURL, update.agentAvatarName);

    this.assignmentType = 'assign';
    this.status = update.status;
    this.selectedAgent = update.agent;
  }

  amPmFormatting() {
    return {
      formatTime: (time) => {
        if (!time || typeof time.hours !== 'number') {
          this.auth.logErrors('Malformed time value on date/time picker component', false);
          console.error('Malformed time value on date/time picker component');
          return '12:00 AM';
        }

        let hours = time.hours;
        const minutes = String(time.minutes).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${hours}:${minutes} ${ampm}`;
      },
      parseTime: (timeString) => {
        const [time, ampm] = timeString.split(' ');
        const [_hours, _minutes] = time.split(':');

        let hours = parseInt(_hours, 10);
        const minutes = parseInt(_minutes, 10);

        const isPM = ampm === 'PM';

        if (isPM && hours < 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;

        return { hours, minutes };
      },
    };
  }
}

function getStartAndEndDates(date: DateWithTimezone, startTime: string, endTime) {
  const [startHour, startMinute] = parseTime(startTime);
  const [endHour, endMinute] = parseTime(endTime);

  const start = date.clone().set({ hours: startHour, minutes: startMinute }) as DateWithTimezone;
  const end = date.clone().set({ hours: endHour, minutes: endMinute }) as DateWithTimezone;

  return {
    start,
    end,
  };
}

function parseTime(rawTime: string) {
  const [hour, minute] = rawTime.split(':');
  return [hour ? Number(hour) : 0, minute ? Number(minute) : 0];
}
